import { useWindowDomEvent } from 'hooks/use-dom-event'
import * as React from 'react'
import { handleColumnResizeKey } from '@repo/studio-components-resize-handle/apply-column-resize-key'
import { useResizableColumn } from '@repo/studio-components-resize-handle/use-resizable-column'
import {
  AIDA_DOCK_MAX_WIDTH_PX,
  AIDA_DOCK_MIN_WIDTH_PX,
  AIDA_DOCK_WIDTH_PX,
  clampDockWidth,
} from '../utils/constants/presentation'
import {
  previewAidaDockWidth,
  setAidaDockWidth,
  useAidaOverlayStore,
} from '../stores/overlay-store'

const KEYBOARD_STEP_PX = 16
const KEYBOARD_SHIFT_STEP_PX = 64
const RESIZE_STYLE_ID = 'aida-dock-resize-style'

const KEYBOARD_DELTA: Record<string, number> = {
  ArrowLeft: 1,
  ArrowUp: 1,
  ArrowRight: -1,
  ArrowDown: -1,
}

export type DockResizeHandle = ReturnType<typeof useDockResizeHandle>

function ensureResizeStyle() {
  if (document.querySelector(`#${RESIZE_STYLE_ID}`)) {
    return
  }
  const style = document.createElement('style')
  style.id = RESIZE_STYLE_ID
  style.textContent =
    '[data-aida-dock-resizing],[data-aida-dock-resizing] *{pointer-events:none !important;transition:none !important}'
  document.head.append(style)
}

function layoutShell() {
  const content = document.querySelector<HTMLElement>('[data-testid="studio-layout-content"]')
  let node = content?.parentElement ?? undefined
  while (node) {
    if (node.classList.contains('absolute') && node.classList.contains('h-screen')) {
      return node
    }
    node = node.parentElement ?? undefined
  }
}

function freezeContent() {
  document.documentElement.style.cursor = 'col-resize'
  document.documentElement.style.userSelect = 'none'
  const shell = layoutShell()
  if (shell) {
    ensureResizeStyle()
    shell.dataset.aidaDockResizing = ''
  }
  return shell
}

function thawContent(shell: HTMLElement | undefined) {
  if (shell) {
    delete shell.dataset.aidaDockResizing
  }
  document.documentElement.style.cursor = ''
  document.documentElement.style.userSelect = ''
}

function applyKeyboardResize(event: React.KeyboardEvent<HTMLDivElement>) {
  const current = useAidaOverlayStore.getState().dockWidth
  if (event.key === 'Home' || event.key === 'End') {
    handleColumnResizeKey(event, {
      currentWidth: current,
      minWidth: AIDA_DOCK_MIN_WIDTH_PX,
      maxWidth: AIDA_DOCK_MAX_WIDTH_PX,
      onChange: setAidaDockWidth,
    })
    return
  }
  const direction = KEYBOARD_DELTA[event.key]
  if (!direction) {
    return
  }
  event.preventDefault()
  const step = event.shiftKey ? KEYBOARD_SHIFT_STEP_PX : KEYBOARD_STEP_PX
  setAidaDockWidth(current + direction * step)
}

export function useDockResizeHandle(enabled: boolean) {
  const dockWidth = useAidaOverlayStore(state => state.dockWidth)
  const originWidth = React.useRef(dockWidth)
  const frozenShell = React.useRef<HTMLElement | undefined>(undefined)
  const [viewportWidth, setViewportWidth] = React.useState(
    typeof window === 'undefined' ? AIDA_DOCK_WIDTH_PX * 2 : window.innerWidth,
  )

  const bounds = {
    min: clampDockWidth(AIDA_DOCK_MIN_WIDTH_PX, viewportWidth),
    max: clampDockWidth(AIDA_DOCK_MAX_WIDTH_PX, viewportWidth),
  }

  const syncViewport = React.useCallback(() => {
    setViewportWidth(window.innerWidth)
  }, [])

  useWindowDomEvent('resize', enabled ? syncViewport : false)

  React.useEffect(() => {
    if (enabled) {
      syncViewport()
    }
  }, [enabled, syncViewport])

  const { isDragging, handleProps } = useResizableColumn({
    currentWidth: dockWidth,
    minWidth: bounds.min,
    maxWidth: bounds.max,
    onChange: previewAidaDockWidth,
    enabled,
    invert: true,
    cancelOnWindowBlur: true,
    onDragStart: () => {
      originWidth.current = useAidaOverlayStore.getState().dockWidth
      frozenShell.current = freezeContent()
    },
    onDragEnd: () => {
      thawContent(frozenShell.current)
      frozenShell.current = undefined
      setAidaDockWidth(useAidaOverlayStore.getState().dockWidth)
    },
    onDragCancel: () => {
      thawContent(frozenShell.current)
      frozenShell.current = undefined
      previewAidaDockWidth(originWidth.current)
    },
  })

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (enabled) {
        applyKeyboardResize(event)
      }
    },
    [enabled],
  )

  const onReset = React.useCallback(() => {
    if (enabled) {
      setAidaDockWidth(AIDA_DOCK_WIDTH_PX)
    }
  }, [enabled])

  return {
    dockWidth,
    bounds,
    isDragging,
    handleProps,
    onKeyDown,
    onReset,
  }
}
